import * as React from 'react';
import { Container, Form, Button } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Sentry from '@sentry/browser';
import { recaptchaSize } from '../../helpers/common';
import { submitFormData } from '../../helpers/form-api';

const recaptchaKey = process.env.GATSBY_GOOGLE_RECAPTCHA_KEY || '';

class ConnectInHomeDeviceForm extends React.Component {
  initialFormData = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    national_meter_identifier_nmi: '',
    meter_number: '',
    account_holder_name: '',
    mac_address: '',
    installation_code: '',
    additional_information: '',
  };

  state = {
    error: null,
    success: null,
    submitting: false,
    formData: { ...this.initialFormData },
  };

  recaptchaRef = React.createRef();

  handleCaptcha = (value) => {
    this.setState({ captcha: value });
  };

  handleInputChange = (event) => {
    const target = event.currentTarget;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  handleSelectChange = (event, { name, value }) => {
    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  handleSubmit = async () => {
    try {
      this.setState({ success: null, error: null, submitting: true });

      if (this.state.captcha) {
        const formData = {
          ...this.state.formData,
          captchaResponse: this.state.captcha,
        };
        const response = await submitFormData(
          'connect_in_home_device',
          formData
        );

        if (response.status !== 200) {
          this.setState({
            success: null,
            error: response.message,
            submitting: false,
            captcha: null,
          });
        } else {
          this.setState({
            success: 'Success',
            error: null,
            submitting: false,
            formData: { ...this.initialFormData },
            captcha: null,
          });
        }

        if (this.recaptchaRef.current) {
          this.recaptchaRef.current.reset();
        }
      } else {
        this.setState({
          success: null,
          error: 'Please tick ReCAPTCHA',
          submitting: false,
        });
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      this.setState({ error: 'Error', success: null, submitting: false });
    }
  };

  submitText = () => {
    if (this.state.submitting) {
      return 'Submitting...';
    } else if (this.state.success) {
      return 'Submitted';
    }
    return 'Submit';
  };

  render() {
    const {
      first_name,
      last_name,
      email,
      phone,
      address,
      national_meter_identifier_nmi,
      meter_number,
      account_holder_name,
      mac_address,
      installation_code,
      additional_information,
    } = this.state.formData;

    return (
      <section>
        <Container>
          <div className="grid-wrapper">
            <div className="form-container">
              <Form onSubmit={this.handleSubmit}>
                <h4>Your Details</h4>

                <Form.Field>
                  <Form.Input
                    label="First name"
                    required
                    name="first_name"
                    type="text"
                    value={first_name}
                    onChange={this.handleInputChange}
                    id="form-first-name"
                  />
                </Form.Field>

                <Form.Field>
                  <Form.Input
                    label="Last name"
                    required
                    type="text"
                    name="last_name"
                    value={last_name}
                    onChange={this.handleInputChange}
                    id="form-last-name"
                  />
                </Form.Field>

                <Form.Field>
                  <Form.Input
                    label="Email address"
                    required
                    type="email"
                    name="email"
                    value={email}
                    onChange={this.handleInputChange}
                    id="form-email"
                  />
                </Form.Field>

                <Form.Field>
                  <Form.Input
                    label="Phone number"
                    required
                    type="text"
                    name="phone"
                    value={phone}
                    onChange={this.handleInputChange}
                    id="form-phone"
                  />
                </Form.Field>

                <h4>Property details</h4>

                <Form.Field>
                  <Form.Input
                    label="Street address"
                    required
                    type="text"
                    name="address"
                    value={address}
                    onChange={this.handleInputChange}
                    id="form-address"
                  />
                </Form.Field>

                <Form.Field>
                  <Form.Input
                    label="National Meter Identifier (NMI)"
                    required
                    type="text"
                    name="national_meter_identifier_nmi"
                    value={national_meter_identifier_nmi}
                    onChange={this.handleInputChange}
                    id="form-national-meter-identifier-nmi"
                  />
                </Form.Field>
                <div className="form-field__description">
                  Your NMI number appears on the bill you receive from your
                  retailer
                </div>

                <Form.Field>
                  <Form.Input
                    label="Meter Number"
                    type="text"
                    name="meter_number"
                    value={meter_number}
                    onChange={this.handleInputChange}
                    required
                    id="form-meter-number"
                  />
                </Form.Field>
                <div className="form-field__description">
                  Your meter number appears on the bill you receive from your
                  retailer
                </div>

                <Form.Field>
                  <Form.Input
                    label="Account Holder Name"
                    type="text"
                    name="account_holder_name"
                    value={account_holder_name}
                    onChange={this.handleInputChange}
                    required
                    id="form-account-holder-name"
                  />
                </Form.Field>
                <div className="form-field__description">
                  The name shown on your electricity bill
                </div>

                <h4>Device details</h4>

                <Form.Field>
                  <Form.Input
                    label="MAC Address"
                    type="text"
                    name="mac_address"
                    value={mac_address}
                    onChange={this.handleInputChange}
                    required
                    id="form-mac-address"
                  />
                </Form.Field>
                <div className="form-field__description">
                  16 characters of digits or A, B, C, D, E and F
                </div>

                <Form.Field>
                  <Form.Input
                    label="Installation Code"
                    type="text"
                    name="installation_code"
                    value={installation_code}
                    onChange={this.handleInputChange}
                    required
                    id="form-installation-code"
                  />
                </Form.Field>
                <div className="form-field__description">
                  16 characters of digits or A, B, C, D, E and F
                </div>

                <Form.Field>
                  <Form.TextArea
                    label="Additional information"
                    name="additional_information"
                    value={additional_information}
                    onChange={this.handleInputChange}
                    id="form-additional-information"
                  />
                </Form.Field>

                <ReCAPTCHA
                  ref={this.recaptchaRef}
                  sitekey={recaptchaKey}
                  onChange={this.handleCaptcha}
                  size={recaptchaSize()}
                />

                <Button
                  className="form--submit-btn"
                  disabled={this.state.submitting}
                  loading={this.state.submitting}
                >
                  {this.submitText()}
                </Button>

                {this.state.success ? (
                  <div className="success-message">{this.state.success}</div>
                ) : null}
                {this.state.error ? (
                  <div className="error-message">{this.state.error}</div>
                ) : null}
              </Form>
            </div>
          </div>
        </Container>
      </section>
    );
  }
}

export default ConnectInHomeDeviceForm;
